import React, { useContext, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { useSnackbar } from "react-simple-snackbar";
import { CandidateContext } from "../../../../context/CandidateContext";
import { API } from "../../../../http-common";
import { UserContext } from "../../../../context/userContext";
import { GROUP } from "../../../../constants";
import { PROSPECT_STATUS } from "../../../../constants-prod";

const STATUS_LABEL = {
  pending: "Pendiente",
  created: "Generado",
  sent: "Enviado",
  signed: "Firmado",
  completed: "Firmado",
};

const SIGNER_ROLE_LABEL = {
  sub: "subusuario",
  parent: "usuario principal",
};

/* Fallback para respuestas sin `signers` */
const buildSignerLabel = (signer, subName, parentName) => {
  const subLabel = subName ? `${subName} (subusuario)` : "(subusuario)";
  const parentLabel = parentName ? `${parentName} (usuario principal)` : "(usuario principal)";

  if (signer === "sub") return subLabel;
  if (signer === "parent") return parentLabel;
  if (signer === "both") return `${subLabel} y ${parentLabel}`;
  return "-";
};

/* Los documentos compartidos se generan recién cuando todos sus subusuarios llegan a "pendiente firma" */
const isReadyForGeneration = (prospectStatus) =>
  prospectStatus !== undefined && Number(prospectStatus) >= PROSPECT_STATUS.SC_CONTRATO_PENDIENTE;

/* Unir / separar documentos entre subusuarios: oculto por hasta terminar implementacion */
const JOIN_DOCUMENTS_ENABLED = false;

export const TypeDocument = ({ check_data, refreshKey }) => {
  const { candidateData } = useContext(CandidateContext);
  const { userData } = useContext(UserContext);
  const canJoinDocuments = JOIN_DOCUMENTS_ENABLED && GROUP.EQ_DIRECTORES.includes(userData.role_id);
  const [openSnackbarError] = useSnackbar({ position: "bottom-right" });
  const [openSnackbarSuccess] = useSnackbar({ position: "bottom-right" });

  const [savedContractType, setSavedContractType] = useState("");
  const [selectedContractType, setSelectedContractType] = useState("");
  const [documents, setDocuments] = useState([]);
  const [subName, setSubName] = useState("");
  const [parentName, setParentName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* Estado de los subusuarios de cada usuario principal, para "esperando a" y para unir documentos */
  const [subsByParent, setSubsByParent] = useState({});

  const [joinDoc, setJoinDoc] = useState(null);
  const [joinSelectedIds, setJoinSelectedIds] = useState([]);
  const [joinLoading, setJoinLoading] = useState(false);
  const [splitLoadingId, setSplitLoadingId] = useState(null);

  const primaryParentId = candidateData?.parent_prospect_id;
  const docParentId = (doc) => doc.parent?.id ?? primaryParentId;

  const fetchSubsOfParent = async (parentId) => {
    const res = await API.get(`/prospects/sub-prospects/${parentId}`);
    return res.data.data || [];
  };

  const loadSubsForSharedDocs = async (docs) => {
    const parentIds = [
      ...new Set(
        docs
          .filter((doc) => doc.status === "pending" && doc.shared_with?.length)
          .map(docParentId)
          .filter(Boolean)
      ),
    ];
    if (!parentIds.length) return;
    try {
      const results = await Promise.all(parentIds.map(fetchSubsOfParent));
      setSubsByParent((prev) => {
        const next = { ...prev };
        parentIds.forEach((id, i) => { next[id] = results[i]; });
        return next;
      });
    } catch (error) {
      // Sin estos datos solo se pierde el detalle de "esperando a"
    }
  };

  const applyDocumentsData = (data) => {
    const docs = data.documents || [];
    setDocuments(docs);
    setSubName(data.sub_name || "");
    setParentName(data.parent_name || "");
    loadSubsForSharedDocs(docs);
  };

  const fetchData = async () => {
    try {
      const res = await API.get(`/prospects/sub-prospects/${candidateData.id}/documents`);
      const contract_type = res.data.data.contract_type || "";
      setSavedContractType(contract_type);
      setSelectedContractType(contract_type);
      applyDocumentsData(res.data.data);
      if (contract_type) {
        check_data?.(true);
      }
    } catch (error) {
      openSnackbarError("Error al obtener los documentos");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (candidateData?.id) fetchData();
  }, [candidateData?.id, candidateData?.prospect_status, primaryParentId, refreshKey]);

  const handleSave = async () => {
    if (!selectedContractType) return;
    setSaving(true);
    try {
      // Cambiar el tipo de contrato saca al sub de los documentos compartidos pendientes; la respuesta ya trae la lista actualizada
      const res = await API.post(`/prospects/sub-prospects/${candidateData.id}/contract-type`, { contract_type: selectedContractType });
      applyDocumentsData(res.data.data);
      setSavedContractType(selectedContractType);
      openSnackbarSuccess("Tipo de contrato guardado");
      check_data?.(true);
    } catch (error) {
      openSnackbarError(error.response?.data?.errors?.[0]?.msg || "No se pudo guardar el tipo de contrato");
    }
    setSaving(false);
  };

  const getSharedWith = (doc) => (doc.shared_with || []).filter((s) => s.id !== candidateData.id);

  const getWaitingFor = (doc) => {
    const subs = subsByParent[docParentId(doc)] || [];
    const statusById = Object.fromEntries(subs.map((s) => [s.id, s.prospect_status]));
    const members = [
      { id: candidateData.id, complete_name: subName || candidateData.complete_name, prospect_status: candidateData.prospect_status },
      ...getSharedWith(doc).map((s) => ({ ...s, prospect_status: statusById[s.id] })),
    ];
    return members.filter((m) => !isReadyForGeneration(m.prospect_status));
  };

  const handleOpenJoin = async (doc) => {
    const parentId = docParentId(doc);
    setJoinSelectedIds([]);
    setJoinDoc(doc);
    if (!parentId || subsByParent[parentId]) return;
    try {
      const subs = await fetchSubsOfParent(parentId);
      setSubsByParent((prev) => ({ ...prev, [parentId]: subs }));
    } catch (error) {
      openSnackbarError("Error al obtener los subusuarios");
    }
  };

  const joinCandidates = joinDoc
    ? (subsByParent[docParentId(joinDoc)] || []).filter(
        (s) => s.id !== candidateData.id && !getSharedWith(joinDoc).some((shared) => shared.id === s.id)
      )
    : [];

  const toggleJoinSelected = (id) => {
    setJoinSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleJoin = async () => {
    setJoinLoading(true);
    try {
      const body = {
        document_type: joinDoc.document_type,
        sub_prospect_ids: [candidateData.id, ...getSharedWith(joinDoc).map((s) => s.id), ...joinSelectedIds],
      };
      if (joinDoc.document_type !== "contract") {
        body.parent_prospect_id = docParentId(joinDoc);
      }
      await API.post(`/prospects/sub-prospects/documents/join`, body);
      openSnackbarSuccess("Documentos unidos correctamente");
      setJoinDoc(null);
      fetchData();
    } catch (error) {
      openSnackbarError(error.response?.data?.errors?.[0]?.msg || "No se pudieron unir los documentos");
    }
    setJoinLoading(false);
  };

  const handleSplit = async (doc) => {
    if (!window.confirm("¿Separar este documento compartido? Cada subusuario volverá a tener su propio documento.")) return;
    setSplitLoadingId(doc.document_id);
    try {
      await API.delete(`/prospects/sub-prospects/documents/${doc.document_id}`);
      openSnackbarSuccess("Documento separado correctamente");
      fetchData();
    } catch (error) {
      openSnackbarError(error.response?.data?.errors?.[0]?.msg || "No se pudo separar el documento");
    }
    setSplitLoadingId(null);
  };

  const renderSigners = (doc) => {
    if (!doc.signers?.length) return buildSignerLabel(doc.signer, subName, parentName);
    return doc.signers.map((s) => (
      <div key={`${s.prospect_id}-${s.role}`}>
        {s.complete_name} ({SIGNER_ROLE_LABEL[s.role] || s.role})
        {" - "}
        <span className={s.status === "signed" ? "text-success" : "text-muted"}>
          {s.status === "signed" ? "Firmado" : "Pendiente"}
        </span>
      </div>
    ));
  };

  return (
    <div className="col-md-12 item my-4">
      <div className="row header_data_section">Documentos del sub-usuario</div>

      <div className="row mt-4 align-items-end">
        <div className="col-md-4 col-xs-12">
          <label className="blue_data" htmlFor="sub_contract_type">Tipo de contrato</label>
          <select
            id="sub_contract_type"
            className="form-control data_item"
            value={selectedContractType}
            onChange={(e) => setSelectedContractType(e.target.value)}
            disabled={loading || saving}
          >
            <option value="" disabled>Seleccionar</option>
            <option value="AP">Productor (AP)</option>
            <option value="REF">Referenciador (REF)</option>
          </select>
        </div>
        <div className="col-md-4 col-xs-12">
          <button
            type="button"
            className="btn blue_btn"
            disabled={loading || saving || !selectedContractType || selectedContractType === savedContractType}
            onClick={handleSave}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      {savedContractType && (
        <table className="table mt-3">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Firma</th>
              <th>Estado</th>
              {canJoinDocuments && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {documents.map((doc) => {
              const sharedWith = getSharedWith(doc);
              const isShared = sharedWith.length > 0;
              const isPending = doc.status === "pending";
              const waitingFor = isShared && isPending ? getWaitingFor(doc) : [];
              return (
                <tr key={doc.document_id ?? doc.document_type}>
                  <td>
                    {doc.label}
                    {doc.parent && <div className="text-muted small">Usuario principal: {doc.parent.complete_name}</div>}
                    {isShared && (
                      <div className="text-muted small">
                        Compartido con: {sharedWith.map((s) => s.complete_name).join(", ")}
                      </div>
                    )}
                  </td>
                  <td>{renderSigners(doc)}</td>
                  <td>
                    {STATUS_LABEL[doc.status] || doc.status}
                    {waitingFor.length > 0 && (
                      <div className="text-muted small">
                        Esperando a: {waitingFor.map((m) => m.complete_name).join(", ")}
                      </div>
                    )}
                  </td>
                  {canJoinDocuments && (
                    <td>
                      {isPending && doc.document_id && (
                        <>
                          <button
                            type="button"
                            className="btn blue_btn btn-sm me-2 mb-1"
                            id={`join_document_btn_${doc.document_id}`}
                            onClick={() => handleOpenJoin(doc)}
                          >
                            Unir con otros subusuarios
                          </button>
                          {isShared && (
                            <button
                              type="button"
                              className="btn gray_btn btn-sm mb-1"
                              id={`split_document_btn_${doc.document_id}`}
                              disabled={splitLoadingId === doc.document_id}
                              onClick={() => handleSplit(doc)}
                            >
                              Separar
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* UNIR DOCUMENTO CON OTROS SUBUSUARIOS MODAL */}
      <Modal isOpen={Boolean(joinDoc)} className="" id="join_document_modal">
        <ModalHeader className="justify-content-end">
          <button className="close bg-transparent border-0" onClick={() => setJoinDoc(null)}>
            <span>&times;</span>
          </button>
        </ModalHeader>
        <ModalBody className="modal-body">
          <p className="modal_title blue_text">
            Unir documento con otros subusuarios
            {joinDoc && <span className="ms-2 name_applicant"><b>{joinDoc.label}</b></span>}
          </p>
          {joinDoc?.parent && <p className="text-muted">Usuario principal: {joinDoc.parent.complete_name}</p>}
          <form action="#">
            {joinCandidates.length > 0 ? (
              joinCandidates.map((s) => (
                <div className="form-check mb-2" key={s.id}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`join_sub_${s.id}`}
                    checked={joinSelectedIds.includes(s.id)}
                    onChange={() => toggleJoinSelected(s.id)}
                  />
                  <label className="form-check-label" htmlFor={`join_sub_${s.id}`}>
                    {s.complete_name}
                  </label>
                </div>
              ))
            ) : (
              <p>No hay otros subusuarios disponibles para unir.</p>
            )}
            <div className="row modal_btns">
              <button
                type="button"
                className="btn blue_btn"
                id="join_document_confirm_btn"
                disabled={!joinSelectedIds.length || joinLoading}
                onClick={handleJoin}
              >
                Confirmar
              </button>
              <button type="button" className="btn red_btn" id="join_document_cancel_btn" onClick={() => setJoinDoc(null)}>
                Cancelar
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};
